import SimCardIcon from '@mui/icons-material/SimCard';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { useLocation, useNavigate } from 'react-router';
import {
  downloadBulkProvisioningInputFile,
  downloadErrorSummary,
} from '../BulkProvisioningApi/bulkProvisioningApi.ts';
import { useDebounce } from 'common/useDebounce.ts';
import { Routes } from '@/Routes.constants';
import { ProvisioningSource, mapSourceValueToLabel } from '../constants/provisioningSource.ts';
import { useTranslation } from 'react-i18next';
import { Actions, Subjects } from 'permissions/ability.ts';
import { ConfigurableTable } from 'common/components/ConfigurableTable/ConfigurableTable.tsx';
import { StatusBadgesCell } from '../Components/Cells/StatusBadgesCell.tsx';
import {
  CounterCell,
  CounterCellFailed,
  CounterCellSuccess,
} from '../Components/Cells/CounterCell.tsx';
import { DateTimeCell } from 'common/components/ConfigurableTable/Components/Cells/DateTimeCell.tsx';
import { useAbility } from 'permissions/hooks/useAbility.ts';
import { BulkProvisioningNoRowsOverlay } from '../Components/BulkProvisioningNoRowsOverlay.tsx';
import { useBulkProvisoningFetch } from './hooks/useBulkProvisioningFetch.ts';
import { useAtom, useAtomValue } from 'jotai';
import { pageFilterAtom, rowsPerPageFilterAtom } from 'atoms/filters/provisioning.ts';
import { ColumnConfiguration } from 'common/components/ConfigurableTable/models/configurableTable.ts';
import { TextCell } from 'common/components/ConfigurableTable/Components/Cells/TextCell.tsx';
import { bulkProvisioningFiltersAtom } from 'atoms/filters/bulk-provisioning.ts';
import { useHasFeatureFlag } from 'featureFlags/useHasFeatureFlag.ts';

export const BulkProvisioningJobsTable = () => {
  const productManagementEnabled = useHasFeatureFlag('ProductManagement');
  const filters = useAtomValue(bulkProvisioningFiltersAtom);

  const { status, uploadDateFrom, uploadDateTo, source, orderNumber, fileName } = filters;
  const [page, setPage] = useAtom(pageFilterAtom);
  const [rowsPerPage, setRowsPerPage] = useAtom(rowsPerPageFilterAtom);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const debouncedFileName = useDebounce(fileName, 200);

  const queryResult = useBulkProvisoningFetch({
    status,
    uploadDateFrom,
    uploadDateTo,
    fileName: debouncedFileName,
    source,
    orderNumber,
    page,
    rowsPerPage,
  });

  const { data: bulkProvisioningJobs } = queryResult;

  const { pathname, search } = useLocation();

  const configuration: ColumnConfiguration[] = [
    {
      field: 'fileName',
      headerName: t('common.fileName'),
      resizable: false,
      sortable: false,
      pinnable: false,
      minWidth: 350,
    },
    {
      field: 'status',
      headerName: t('common.status'),
      resizable: false,
      sortable: false,
      pinnable: false,
      width: 246,
    },
    {
      field: 'numberOfCards',
      headerName: t('common.numberOfCards'),
      resizable: false,
      sortable: false,
      pinnable: false,
      minWidth: 128,
    },
    {
      field: 'numberOfSuccessCards',
      headerName: t('common.success'),
      resizable: false,
      sortable: false,
      pinnable: false,
    },
    {
      field: 'numberOfFailedCards',
      headerName: t('common.failed'),
      resizable: false,
      sortable: false,
      pinnable: false,
    },
    {
      field: 'uploadAt',
      headerName: t('common.uploadTime'),
      resizable: false,
      sortable: false,
      pinnable: false,
      minWidth: 196,
    },
    {
      field: 'uploadFinishedAt',
      headerName: t('bulkProvisioning.processingFinishedTime'),
      resizable: false,
      sortable: false,
      pinnable: false,
      minWidth: 196,
    },
    {
      field: 'source',
      headerName: t('common.source'),
      valueGetter: (value: ProvisioningSource) =>
        value === null ? null : mapSourceValueToLabel(value, t),
      resizable: false,
      sortable: false,
      pinnable: false,
    },
    {
      field: 'orderNumber',
      headerName: t('common.orderNumber'),
      resizable: false,
      sortable: false,
      pinnable: false,
      minWidth: 120,
    },
    {
      field: 'uploadBy',
      headerName: t('simInventory.userName'),
      resizable: false,
      sortable: false,
      pinnable: false,
      minWidth: 120,
      flex: 1,
    },
  ];

  const ability = useAbility();

  return (
    <ConfigurableTable
      disableColumnReorder
      actions={[
        {
          label: t('bulkProvisioning.viewDetails'),
          icon: <SimCardIcon />,
          actionCallback: (data: any) => {
            const path = productManagementEnabled
              ? `${Routes.simProvisioning}/${data.id}${search}`
              : `${Routes.provisioningRequest}/${data.id}${search}`;

            navigate(path, {
              state: { previousPath: `${pathname}${search}` },
            });
          },
        },
        {
          label: t('bulkProvisioning.downloadInput'),
          icon: <DownloadIcon />,
          enabled: ability.can(Actions.export, Subjects.bulkProvisioning),
          actionCallback: (data: any) => {
            downloadBulkProvisioningInputFile(data.id);
          },
          slotProps: {
            menuItem: {
              'data-testid': 'downloadInputFileButton',
            },
          },
        },
        {
          label: t('bulkProvisioning.downloadFailedResults'),
          icon: <CloseIcon />,
          enabled: (data: any) => !!data.errorSummaryFileName,
          actionCallback: (data: any) => {
            downloadErrorSummary(data.id);
          },
        },
      ]}
      data-testid="bulk provisioning table"
      configuration={configuration}
      rows={bulkProvisioningJobs?.items || []}
      cellConfiguration={{
        numberOfCards: CounterCell,
        numberOfSuccessCards: CounterCellSuccess,
        numberOfFailedCards: CounterCellFailed,
        status: StatusBadgesCell,
        uploadAt: DateTimeCell,
        uploadFinishedAt: DateTimeCell,
        default: TextCell,
      }}
      totalNumberOfItems={bulkProvisioningJobs?.totalNumberOfItems ?? 0}
      onPaginationChange={(pagination) => {
        if (pagination.page !== page) {
          setPage(pagination.page);
        }

        if (pagination.pageSize !== rowsPerPage) {
          setRowsPerPage(pagination.pageSize);
        }
      }}
      page={page}
      pageSize={rowsPerPage}
      slots={{
        noRowsOverlay: BulkProvisioningNoRowsOverlay,
      }}
    />
  );
};
